import type { GuidedConversationView, GuidedEvent } from './guided-reporting';
import { EiApplicationInput, EiApplicationPreFill, EiClaim, EiReport, EiReportingStatus } from './models';

/**
 * Employment Insurance's feature components depend on this abstraction,
 * never on a concrete HTTP implementation directly -- see CLAUDE.md's
 * "Apps are thin, libraries hold the real functionality" section.
 *
 * No Angular `InjectionToken` here (unlike this file's pre-React-port
 * history) -- App.tsx passes the concrete client down as a plain prop,
 * not through Angular DI, and this library must stay resolvable from a
 * bundle with no Angular installed at all.
 */
export interface EmploymentInsuranceApiClient {
  applyForEi(applicantSub: string, application: EiApplicationInput): Promise<EiClaim>;
  /**
   * Insurable totals for the wizard's confirm step. Takes no applicantSub:
   * unlike the stub routes above, the BFF derives the citizen from the
   * verified bearer token, because this one reaches a real upstream service
   * keyed on that sub.
   */
  getApplicationPreFill(): Promise<EiApplicationPreFill>;
  getClaim(applicantSub: string): Promise<EiClaim | null>;
  getReportingStatus(applicantSub: string): Promise<EiReportingStatus | null>;
  submitReport(
    claimId: string,
    applicantSub: string,
    periodStart: string,
    periodEnd: string,
    workedHours: number,
    earnings: number,
  ): Promise<EiReport>;
  /**
   * Demo-only "end claim" (docs/specs/016-ei-reporting-flow.md): removes the
   * signed-in citizen's claim and reports. Takes no applicantSub -- the BFF
   * acts only on the sub in the verified bearer token. Idempotent.
   */
  endClaim(): Promise<void>;
  /**
   * EI guided reporting (docs/specs/018-ei-guided-reporting.md). The BFF
   * owns the conversation and derives the citizen from the verified bearer
   * token: these take only the scheduled period's start. Each returns the
   * full state to render; the browser never computes a check.
   */
  getGuidedConversation(periodStart: string): Promise<GuidedConversationView>;
  sendGuidedTurn(periodStart: string, event: GuidedEvent): Promise<GuidedConversationView>;
  /** Writes the report from the answers the BFF holds. */
  submitGuidedReport(periodStart: string): Promise<EiReport>;
}
